import React, { forwardRef, useEffect, useState } from 'react';

import { Slider } from '@vlabs/uikit';
import { Input } from '@vlabs/uikit/components/controls/input';

import type { PopoverWithButtonProps } from '@vlabs/monokit/shared/ui/popovers';
import { PopoverWithButton } from '@vlabs/monokit/shared/ui/popovers';

import { useEventListFiltersTranslation } from '../../i18n';
import {
  getSimilarityFilters,
  getSimilarityPercent,
  MAX_SIMILARITY_PERCENT,
  MIN_SIMILARITY_PERCENT,
} from '../../similarity';
import { useEventListFilters } from '../../useEventListFilters';
import st from '../popovers.module.sass';

const SIMILARITY_STEP = 1;

const NUMERIC_PATTERN = /^-?\d+$/;

function SimilarityFilterPopover(
  props: PopoverWithButtonProps,
  ref: React.ForwardedRef<HTMLDivElement>,
) {
  const { t } = useEventListFiltersTranslation();
  const { filters, updateFilters, updateFiltersDebounced } = useEventListFilters();

  const [range, setRange] = useState<[number, number]>([MIN_SIMILARITY_PERCENT, MAX_SIMILARITY_PERCENT]);
  const [gteText, setGteText] = useState<string>(String(MIN_SIMILARITY_PERCENT));
  const [ltText, setLtText] = useState<string>(String(MAX_SIMILARITY_PERCENT));
  const [gteError, setGteError] = useState<string>();
  const [ltError, setLtError] = useState<string>();

  useEffect(() => {
    const gte = getSimilarityPercent(filters.top_similar_object_similarity__gte, MIN_SIMILARITY_PERCENT);
    const lt = getSimilarityPercent(filters.top_similar_object_similarity__lt, MAX_SIMILARITY_PERCENT);

    setRange([gte, lt]);
    setGteText(String(gte));
    setLtText(String(lt));
    setGteError(undefined);
    setLtError(undefined);
  }, [filters]);

  const validateBound = (value: string, min: number, max: number): string | undefined => {
    if (!NUMERIC_PATTERN.test(value)) return t('validation_number') ?? undefined;
    const parsed = Number(value);
    if (parsed < min || parsed > max) return t('validation_range', { left: min, right: max }) ?? undefined;
    return undefined;
  };

  const commitRange = ([gte, lt]: [number, number]) => {
    setRange([gte, lt]);
    updateFiltersDebounced(getSimilarityFilters(gte, lt));
  };

  const handleSliderChange = (value: number | number[]) => {
    if (!Array.isArray(value)) return;

    const [gte, lt] = value as [number, number];
    setGteText(String(gte));
    setLtText(String(lt));
    setGteError(undefined);
    setLtError(undefined);
    commitRange([gte, lt]);
  };

  const handleGteInput = (value: string) => {
    setGteText(value);

    const error = validateBound(value, MIN_SIMILARITY_PERCENT, range[1]);
    setGteError(error);
    if (error) return;

    commitRange([Number(value), range[1]]);
  };

  const handleLtInput = (value: string) => {
    setLtText(value);

    const error = validateBound(value, range[0], MAX_SIMILARITY_PERCENT);
    setLtError(error);
    if (error) return;

    commitRange([range[0], Number(value)]);
  };

  const reset = () => {
    setGteError(undefined);
    setLtError(undefined);
    updateFilters({
      top_similar_object_similarity__gte: undefined,
      top_similar_object_similarity__lt: undefined,
    });
  };

  const error = gteError ?? ltError;

  return (
    <PopoverWithButton
      {...props}
      isButtonDisabled={
        filters.top_similar_object_similarity__gte === undefined
        && filters.top_similar_object_similarity__lt === undefined
      }
      onClickReset={reset}
      ref={ref}
      title={<div className="Subtitle-2">{`${t('top_similar_object_similarity')} %`}</div>}
    >
      <div className={st.SimilarityFilterPopoverContent}>
        <Input
          className={st.TextInput}
          hasError={!!gteError}
          id="top_similar_object_similarity__gte"
          name="top_similar_object_similarity__gte"
          onChangeValue={handleGteInput}
          value={gteText}
        />
        <Slider
          className={st.Slider}
          max={MAX_SIMILARITY_PERCENT}
          min={MIN_SIMILARITY_PERCENT}
          onChange={handleSliderChange}
          range
          step={SIMILARITY_STEP}
          value={range}
        />
        <Input
          className={st.TextInput}
          hasError={!!ltError}
          id="top_similar_object_similarity__lt"
          name="top_similar_object_similarity__lt"
          onChangeValue={handleLtInput}
          value={ltText}
        />
      </div>
      {error ? <div className={st.ValidationError}>{error}</div> : null}
    </PopoverWithButton>
  );
}

const SimilarityFilterPopoverWithRef = forwardRef(SimilarityFilterPopover);
export { SimilarityFilterPopoverWithRef as SimilarityFilterPopover };
